// client/src/crew/schedule.ts — the crew calendar's pure logic (Task C5). No Date constructor on
// calendar days: every day is an IST 'YYYY-MM-DD' string and arithmetic goes through
// shared/vmsDuty/civilDate. "Today" is INJECTED as the IST date (the owner may open the app in the
// UAE; the device date must never decide what is upcoming).
import { addDays, mondayOf, parseIso, weekdayIndex } from "@shared/vmsDuty/civilDate";
import { istInstant } from "@shared/eventWindow";
import { GOOGLE_MAPS_PLACE_URL } from "@shared/venueFacts";

export interface CrewEvent {
  weddingId: number; serviceType: string; date: string; slot: "day" | "evening" | "other"; slotLabel: string;
  ceremonyStart: string | null; couple: string; venue: string; guestCount: number | null; variant: string | null;
  eventManager: { name: string; phone: string };
  /** 2026-11-01. Optional: a server older than the app omits them. */
  brideName?: string | null; groomName?: string | null; clientName?: string | null;
  /** MCs only (owner 2026-11-01). Absent — not null — for everyone else. */
  client?: { name: string; phone: string | null };
  canCallClient?: boolean;
}
export interface CrewShift { date: string; kind: "duty" | "off"; duty: string | null; role: "mentor" | "learner" | "solo" | null; gardeningEvening: boolean; weekStart: string }
export interface CrewShiftWeek { weekStart: string; offDay: string | null; note: string | null }
export interface CrewSchedule { events: CrewEvent[]; shifts: CrewShift[]; shiftWeeks?: CrewShiftWeek[]; generatedAt: string }

export interface DayBucket { date: string; events: CrewEvent[]; shifts: CrewShift[] }

export const SERVICE_LABEL: Record<string, string> = {
  mc_anchor: "MC", fusion_music: "Fusion music", hostesses: "Hostesses", dance_program: "Dance",
  singarimelam: "Singarimelam", duty_roster: "Duty roster", temp_staff: "Event staff",
};
export const serviceLabel = (s: string) => SERVICE_LABEL[s] ?? s.replace(/_/g, " ");

/** Events + shifts merged into one bucket per IST day, ascending. */
export function dayBuckets(s: Pick<CrewSchedule, "events" | "shifts">): DayBucket[] {
  const m = new Map<string, DayBucket>();
  const get = (d: string) => { let b = m.get(d); if (!b) { b = { date: d, events: [], shifts: [] }; m.set(d, b); } return b; };
  for (const e of s.events) get(e.date).events.push(e);
  for (const sh of s.shifts) get(sh.date).shifts.push(sh);
  return Array.from(m.values()).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Upcoming = today (IST) and later, ascending. Past = before today, most recent first. */
export function splitUpcoming(buckets: DayBucket[], todayIST: string): { upcoming: DayBucket[]; past: DayBucket[] } {
  return {
    upcoming: buckets.filter((b) => b.date >= todayIST),
    past: buckets.filter((b) => b.date < todayIST).reverse(),
  };
}

/** Mon–Sun of the ISO week containing `dateIso` (IST calendar). */
export function isoWeekDays(dateIso: string): string[] {
  const mon = mondayOf(dateIso);
  return Array.from({ length: 7 }, (_, i) => addDays(mon, i));
}

/** The week page's title: "This week" only for the week that contains today (audit CREW-4). */
export function weekTitle(days: string[], todayIST: string): string {
  if (days.includes(todayIST)) return "This week";
  const thisMon = isoWeekDays(todayIST)[0];
  if (days[0] === addDays(thisMon, 7)) return "Next week";
  if (days[0] === addDays(thisMon, -7)) return "Last week";
  const [, m, d] = days[0].split("-").map(Number);
  return `Week of ${d} ${MONTH_SHORT[m - 1]}`;
}
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Month grid: whole Mon-first weeks covering the month; days outside it are null. */
export function monthGrid(year: number, month: number): Array<Array<string | null>> {
  const first = `${year}-${String(month).padStart(2, "0")}-01`;
  const weeks: Array<Array<string | null>> = [];
  let cursor = mondayOf(first);
  while (true) {
    const week = Array.from({ length: 7 }, (_, i) => {
      const d = addDays(cursor, i);
      return parseIso(d).m === month ? d : null;
    });
    if (week.every((d) => d === null) && weeks.length) break;
    weeks.push(week);
    cursor = addDays(cursor, 7);
    if (parseIso(cursor).m !== month && parseIso(addDays(cursor, -1)).m !== month) break;
  }
  return weeks.filter((w) => w.some(Boolean));
}

/**
 * Sunday-first month grid — the week every other LA Mirage calendar uses (the manager
 * calendars, the booking calendar). Whole weeks; days outside the month are null.
 */
export function monthGridSunday(year: number, month: number): Array<Array<string | null>> {
  const mm = String(month).padStart(2, "0");
  const n = new Date(Date.UTC(year, month, 0)).getUTCDate(); // civil arithmetic only
  const lead = (weekdayIndex(`${year}-${mm}-01`) + 1) % 7; // weekdayIndex is Mon = 0
  const cells: Array<string | null> = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= n; d++) cells.push(`${year}-${mm}-${String(d).padStart(2, "0")}`);
  while (cells.length % 7) cells.push(null);
  const weeks: Array<Array<string | null>> = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
export const WEEKDAY_LETTER_SUN = ["S", "M", "T", "W", "T", "F", "S"];
export const WEEKDAY_NAME_SUN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** The fusion calendar's selected date: the picked one in this month; else today; else the first event; else the 1st. */
export function fusionDefaultDay(
  month: string, today: string,
  days: ReadonlyArray<{ date: string; day: { kind: string }; evening: { kind: string }; unslotted: number }>,
  picked: string | null,
): string {
  if (picked && picked.startsWith(`${month}-`)) return picked;
  if (today.startsWith(`${month}-`)) return today;
  const first = days.find((d) => d.date.startsWith(`${month}-`) && (d.day.kind === "event" || d.evening.kind === "event" || d.unslotted > 0));
  return first?.date ?? `${month}-01`;
}

/** Days in the bucket list that should carry a dot on the month grid. */
export const dotDays = (buckets: DayBucket[]) => new Set(buckets.filter((b) => b.events.length || b.shifts.some((s) => s.kind === "duty")).map((b) => b.date));

/** More than one service type across the schedule → rows carry a service badge. */
export const needsServiceBadge = (events: CrewEvent[]) => new Set(events.map((e) => e.serviceType)).size > 1;

export const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const weekdayOf = (d: string) => WEEKDAY_SHORT[weekdayIndex(d)];

/** Slot → local IST wall-clock window, for the calendar file. null = all-day. */
export function slotWindow(slot: CrewEvent["slot"]): { start: string; end: string } | null {
  if (slot === "day") return { start: "110000", end: "150000" };
  if (slot === "evening") return { start: "180000", end: "220000" };
  return null;
}

const icsEscape = (s: string) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");

/** A one-event iCalendar file for "Add to calendar" (times are IST wall clock, TZID=Asia/Kolkata). */
export function icsFor(e: CrewEvent, nowStamp: string): string {
  const day = e.date.replace(/-/g, "");
  const w = slotWindow(e.slot);
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//LA Mirage//Crew app//EN", "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:lamirage-crew-${e.weddingId}-${e.serviceType}@lamirage.co`,
    `DTSTAMP:${nowStamp}`,
    ...(w
      ? [`DTSTART;TZID=Asia/Kolkata:${day}T${w.start}`, `DTEND;TZID=Asia/Kolkata:${day}T${w.end}`]
      : [`DTSTART;VALUE=DATE:${day}`, `DTEND;VALUE=DATE:${addDays(e.date, 1).replace(/-/g, "")}`]),
    `SUMMARY:${icsEscape(`${serviceLabel(e.serviceType)} — ${e.couple || "LA Mirage event"}`)}`,
    `LOCATION:${icsEscape(e.venue || "LA Mirage Koratty")}`,
    `DESCRIPTION:${icsEscape([e.slotLabel, e.ceremonyStart ? `Ceremony ${e.ceremonyStart}` : "", `Event manager: ${e.eventManager.name} ${e.eventManager.phone}`].filter(Boolean).join("\n"))}`,
    "END:VEVENT", "END:VCALENDAR",
  ];
  return lines.join("\r\n") + "\r\n";
}

export const icsDataUrl = (ics: string) => `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
/** The venue's own Google Maps place (shared/venueFacts) — opens the Maps app, not a search page. */
export const DIRECTIONS_URL = GOOGLE_MAPS_PLACE_URL;

/** Detail title: "Bride & Groom" when both are known; else the couple string, then the client's name. */
export function eventTitle(e: Pick<CrewEvent, "couple"> & Partial<Pick<CrewEvent, "brideName" | "groomName" | "clientName">>): string {
  const bride = (e.brideName ?? "").trim();
  const groom = (e.groomName ?? "").trim();
  if (bride && groom) return `${bride} & ${groom}`;
  return (e.couple ?? "").trim() || (e.clientName ?? "").trim() || "LA Mirage event";
}

const utcStamp = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

/**
 * "Add to calendar" as a Google Calendar template link (2026-11-01). The data:.ics file it replaces
 * navigated the app's WebView to a raw text file on Android. Same window as icsFor (slotWindow, IST
 * wall clock), converted to UTC with istInstant — TZ-independent, never the device clock.
 */
export function googleCalendarUrl(e: CrewEvent): string {
  const w = slotWindow(e.slot);
  const hm = (s: string) => [Number(s.slice(0, 2)), Number(s.slice(2, 4))] as const;
  const dates = w
    ? `${utcStamp(istInstant(e.date, ...hm(w.start)))}/${utcStamp(istInstant(e.date, ...hm(w.end)))}`
    : `${e.date.replace(/-/g, "")}/${addDays(e.date, 1).replace(/-/g, "")}`;
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: `${serviceLabel(e.serviceType)} — ${eventTitle(e)}`,
    dates,
    location: e.venue || "LA Mirage Koratty",
    details: [e.slotLabel, e.ceremonyStart ? `Ceremony ${e.ceremonyStart}` : "", `Event manager: ${e.eventManager.name} ${e.eventManager.phone}`].filter(Boolean).join("\n"),
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

const phoneDigits = (p: string | null | undefined) => String(p ?? "").replace(/\D/g, "");
/** tel: link from a stored phone ("+91 94467 76666" → "tel:+919446776666"); null when there is no number. */
export function telHref(p: string | null | undefined): string | null {
  const d = phoneDigits(p);
  return d ? `tel:${String(p).trim().startsWith("+") ? "+" : ""}${d}` : null;
}
/** WhatsApp chat link (opens the WhatsApp app through systemOpen); null when there is no number. */
export function waHref(p: string | null | undefined): string | null {
  const d = phoneDigits(p);
  return d ? `https://wa.me/${d}` : null;
}

/** Web paths that belong to a crew-token session: home, the crew screens and the sign-in screens (CREW-3). */
export function isCrewWebPath(pathname: string): boolean {
  return pathname === "/" || /^\/(crew|login)(\/|$)/.test(pathname);
}

/**
 * Which branch a crew screen should render for a query. Data wins, then error, else loading.
 *
 * Do NOT gate on `isLoading`: in TanStack Query v5 `isLoading` is `isPending && isFetching`, so a
 * query that is pending but NOT fetching — paused because the WebView reports offline at launch, or
 * not started yet — has isLoading=false, error=null and data=undefined. The screens then did
 * `dayBuckets(q.data!)` and crashed with "undefined is not an object (evaluating 's.events')"
 * (Sentry 7764600009, /crew in the iOS app).
 */
export function queryPhase(q: { data?: unknown; error?: unknown }): "ready" | "error" | "loading" {
  if (q.data !== undefined && q.data !== null) return "ready";
  if (q.error) return "error";
  return "loading";
}
